import { useEffect, useState } from "react";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

export type UniquenessState = {
  /** True while a check is in flight for this field. */
  checking: boolean;
  /** Empty when free, otherwise a user-friendly error to render under the field. */
  error: string;
};

/**
 * Debounced cross-restaurant uniqueness check for company phone or email.
 * - Skips empty values (returns no error).
 * - For phone, only fires when the digit count is 10 (so we don't ask the
 *   server about partially-typed numbers).
 * - Excludes the current shop id when editing so we don't conflict with self.
 *
 * Hits POST /shops/check-uniqueness which returns { taken, conflictName }.
 */
export function useShopUniquenessCheck(
  field: "phone" | "email",
  rawValue: string,
  excludeShopId?: string | number,
  delayMs = 350,
): UniquenessState {
  const [state, setState] = useState<UniquenessState>({ checking: false, error: "" });

  useEffect(() => {
    const value = (rawValue || "").trim();
    if (!value) {
      setState({ checking: false, error: "" });
      return;
    }
    // Phone: only check once we have a complete 10-digit number.
    if (field === "phone" && value.replace(/\D+/g, "").length !== 10) {
      setState({ checking: false, error: "" });
      return;
    }
    // Email: only check once it looks like an email (skip half-typed).
    if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setState({ checking: false, error: "" });
      return;
    }

    const ctrl = new AbortController();
    setState({ checking: true, error: "" });
    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/shops/check-uniqueness`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
          body: JSON.stringify({
            field,
            value,
            excludeShopId: excludeShopId ? Number(excludeShopId) : undefined,
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) { setState({ checking: false, error: "" }); return; }
        const json = await res.json();
        if (json?.taken) {
          const name = json?.conflictName ? ` "${json.conflictName}"` : "";
          setState({
            checking: false,
            error: field === "phone"
              ? `Company phone is already used by another restaurant${name}`
              : `Company email is already used by another restaurant${name}`,
          });
        } else {
          setState({ checking: false, error: "" });
        }
      } catch {
        // aborted or network error — stay silent so we don't blame the user
        setState({ checking: false, error: "" });
      }
    }, delayMs);

    return () => { clearTimeout(t); ctrl.abort(); };
  }, [field, rawValue, excludeShopId, delayMs]);

  return state;
}
